'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import Breadcrumbs from '@mui/material/Breadcrumbs'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'

import { RECORD_SKELETON_WIDTH } from '@components/BamaRecordTitle'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useRecordSummary } from '@/hooks/useRecordSummary'
import { LIST_ROUTE_SUFFIX, MODULE_GROUPS, MODULE_LABELS } from '@/utils/constants/navigationLabels'
import { getTabLabel, parseAdminPath } from '@/utils/navigation'

type Crumb = { label: string; href?: string; title?: string; loading?: boolean }

const APP_TITLE = 'Bama'
const HOME_CRUMB: Crumb = { label: 'Inicio', href: '/admin' }
const DEPTH_WITH_PARENT_TAB = 3

const CrumbItem = ({ crumb, isLast }: { crumb: Crumb; isLast: boolean }) => {
  if (crumb.loading) return <Skeleton variant='text' width={RECORD_SKELETON_WIDTH} />

  if (crumb.href) {
    return (
      <Typography
        component={Link}
        href={crumb.href}
        title={crumb.title}
        variant='body2'
        color='primary'
        className='hover:underline'
      >
        {crumb.label}
      </Typography>
    )
  }

  return (
    <Typography
      title={crumb.title}
      variant='body2'
      color={isLast ? 'text.primary' : 'text.secondary'}
      className={isLast ? 'font-medium' : undefined}
    >
      {crumb.label}
    </Typography>
  )
}

const BamaBreadcrumbs = () => {
  const pathname = usePathname()

  const parsed = parseAdminPath(pathname)
  const moduleKey = parsed?.moduleKey
  const basePath = parsed?.basePath ?? ''
  const rest = parsed?.rest ?? []
  const [first, second, third] = rest
  const recordId = first && /^\d+$/.test(first) ? first : undefined
  const record = useRecordSummary(moduleKey, recordId)
  const moduleLabel = moduleKey ? MODULE_LABELS[moduleKey] : undefined
  const listHref = `${basePath}${LIST_ROUTE_SUFFIX}`

  const crumbs: Crumb[] = []

  if (moduleKey && moduleLabel) {
    const group = MODULE_GROUPS[moduleKey]

    crumbs.push(HOME_CRUMB)

    if (group) crumbs.push({ label: group })

    crumbs.push({ label: moduleLabel, href: listHref })

    if (first === 'crear') {
      crumbs.push({ label: 'Nuevo' })
    } else if (recordId) {
      // El código es lo que reconoce el usuario; el nombre queda en el tooltip.
      crumbs.push({
        label: record.code || record.name || `#${recordId}`,
        title: record.code && record.name ? `${record.code} : ${record.name}` : undefined,
        loading: record.loading,
        href: `${basePath}/${recordId}`
      })

      if (second) {
        crumbs.push({
          label: getTabLabel(moduleKey, second) ?? (second === 'editar' ? 'Editar' : second),
          href: third ? `${basePath}/${recordId}/${second}` : undefined
        })
      }

      if (third) crumbs.push({ label: third === 'crear' ? 'Nueva' : `#${third}` })
    }
  }

  // La última migaja es la pantalla actual y no lleva enlace.
  const trail = crumbs.map((crumb, index) => (index === crumbs.length - 1 ? { ...crumb, href: undefined } : crumb))
  const lastCrumb = trail[trail.length - 1]
  const isListPage = lastCrumb?.label === moduleLabel
  const showsModuleOnly = !lastCrumb || isListPage || lastCrumb.loading

  usePageTitle(
    showsModuleOnly
      ? `${moduleLabel ?? 'Bama Admin'}${moduleLabel ? ` · ${APP_TITLE}` : ''}`
      : `${lastCrumb.label} · ${moduleLabel} · ${APP_TITLE}`
  )

  if (trail.length === 0) return null

  // "Volver" va al padre, no al historial: al listado desde el detalle y sus pestañas, y al padre inmediato más abajo.
  const linkableCrumbs = trail.filter(crumb => crumb.href)

  const parentCrumb =
    rest.length >= DEPTH_WITH_PARENT_TAB
      ? linkableCrumbs[linkableCrumbs.length - 1]
      : linkableCrumbs.find(crumb => crumb.href === listHref)

  const backTarget = isListPage ? undefined : parentCrumb

  return (
    <div className='flex items-center flex-wrap gap-x-4 gap-y-1'>
      <Breadcrumbs separator='›' aria-label='Ruta de navegación'>
        {trail.map((crumb, index) => (
          <CrumbItem key={`${crumb.label}-${index}`} crumb={crumb} isLast={index === trail.length - 1} />
        ))}
      </Breadcrumbs>
      {backTarget?.href && (
        <Typography
          component={Link}
          href={backTarget.href}
          variant='body2'
          color='primary'
          className='flex items-center gap-1 hover:underline'
        >
          <i className='tabler-arrow-left text-base' />
          Volver a {backTarget.label}
        </Typography>
      )}
    </div>
  )
}

export default BamaBreadcrumbs
